import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import AdminNewSidebar from "components/admin/AdminNewSidebar";
import { Header } from "components";
import apis from "services";

const PLATFORM_OPTIONS = {
  mobile: {
    label: "Mobile",
    appArea: "mobile",
    entityLabel: "Screen",
    entityKey: "screen",
    topSectionTitle: "Top Visited Screens",
    entityPlaceholder: "Filter by screen label",
  },
  web: {
    label: "Web",
    appArea: "frontend",
    entityLabel: "Page",
    entityKey: "page",
    topSectionTitle: "Top Visited Pages",
    entityPlaceholder: "Filter by page label",
  },
};

const INITIAL_FILTERS = {
  startDate: "",
  endDate: "",
  eventType: "",
  label: "",
};

const EMPTY_SUMMARY = {
  totals: {
    events: 0,
    uniqueSessions: 0,
    uniqueUsers: 0,
  },
  topPages: [],
  topScreens: [],
  topCtas: [],
  eventTypeCounts: [],
};

const EMPTY_EVENTS = {
  total: 0,
  events: [],
};

const EMPTY_FLOWS = {
  totalSessions: 0,
  flows: [],
};

const sectionCardSx = {
  height: "100%",
  borderRadius: 4,
  border: "1px solid rgba(15, 23, 42, 0.08)",
  boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
};

const buttonActiveSx = {
  background: "linear-gradient(135deg, #E60E10 0%, #ff6b4a 100%)",
  boxShadow: "none",
  "&:hover": {
    background: "linear-gradient(135deg, #cc0d0f 0%, #ff5b35 100%)",
    boxShadow: "none",
  },
};

const buttonInactiveSx = {
  borderColor: "rgba(15, 23, 42, 0.14)",
  color: "#111827",
  "&:hover": {
    borderColor: "#E60E10",
    backgroundColor: "rgba(230, 14, 16, 0.04)",
  },
};

const toIsoBoundary = (dateValue, endOfDay = false) =>
  dateValue
    ? `${dateValue}T${endOfDay ? "23:59:59.999" : "00:00:00.000"}Z`
    : undefined;

const formatDateTime = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : "N/A";
};

const formatDuration = (start, end) => {
  const durationMs = new Date(end).getTime() - new Date(start).getTime();
  if (!start || !end || Number.isNaN(durationMs) || durationMs < 0) {
    return "N/A";
  }

  const totalSeconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

const getEventLabel = (event, entityKey) =>
  event?.[entityKey] ||
  event?.route ||
  event?.ctaName ||
  event?.buttonLabel ||
  event?.action ||
  "Unlabeled";

const TrackingAnalytics = () => {
  const [platform, setPlatform] = useState("mobile");
  const [draftFilters, setDraftFilters] = useState(INITIAL_FILTERS);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [eventsData, setEventsData] = useState(EMPTY_EVENTS);
  const [flowsData, setFlowsData] = useState(EMPTY_FLOWS);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const platformConfig = PLATFORM_OPTIONS[platform];

  const requestParams = useMemo(() => {
    const params = {
      platform,
      appArea: platformConfig.appArea,
      pageNumber: 1,
      limit: 12,
    };

    if (filters.startDate) params.startDate = toIsoBoundary(filters.startDate);
    if (filters.endDate) params.endDate = toIsoBoundary(filters.endDate, true);
    if (filters.eventType.trim()) {
      params.eventType = filters.eventType.trim().toLowerCase();
    }
    if (filters.label.trim()) {
      params[platformConfig.entityKey] = filters.label.trim();
    }

    return params;
  }, [filters, platform, platformConfig.appArea, platformConfig.entityKey]);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");

    try {
      const [summaryResponse, eventsResponse, flowsResponse] = await Promise.all([
        apis.getJourneySummary(requestParams),
        apis.getJourneyEvents(requestParams),
        apis.getJourneyFlows({
          ...requestParams,
          limit: 8,
          includeEventsLimit: 8,
        }),
      ]);

      setSummary(summaryResponse?.data?.data || EMPTY_SUMMARY);
      setEventsData(eventsResponse?.data?.data || EMPTY_EVENTS);
      setFlowsData(flowsResponse?.data?.data || EMPTY_FLOWS);
    } catch (error) {
      const message = error?.message || "Unable to load journey analytics";
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [requestParams]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const topVisitedItems =
    platform === "mobile" ? summary?.topScreens || [] : summary?.topPages || [];

  const statCards = [
    {
      title: "Total Events",
      value: Number(summary?.totals?.events || 0).toLocaleString(),
      helper: `${platformConfig.label} events in the selected range`,
    },
    {
      title: "Unique Sessions",
      value: Number(summary?.totals?.uniqueSessions || 0).toLocaleString(),
      helper: "Grouped user journeys available below",
    },
    {
      title: "Known Users",
      value: Number(summary?.totals?.uniqueUsers || 0).toLocaleString(),
      helper: "Authenticated users tied to events",
    },
    {
      title: "CTA Interactions",
      value: Number(
        (summary?.topCtas || []).reduce((total, item) => total + (item?.count || 0), 0)
      ).toLocaleString(),
      helper: "Combined top CTA and button activity",
    },
  ];

  const handleDraftChange = (key) => (event) => {
    setDraftFilters((previous) => ({
      ...previous,
      [key]: event.target.value,
    }));
  };

  const handlePlatformChange = (nextPlatform) => {
    setPlatform(nextPlatform);
    setDraftFilters((previous) => ({ ...previous, label: "" }));
    setFilters((previous) => ({ ...previous, label: "" }));
  };

  return (
    <Box className="dashboard-main">
      <AdminNewSidebar />
      <div className="dashboard-section-main">
        <Box
          component="main"
          sx={{ flexGrow: 1, height: "100vh", overflow: "auto" }}
          className="custom-scroll"
        >
          <Header
            showDropdown={showDropdown}
            setShowDropdown={setShowDropdown}
          />

          {loading ? (
            <div className="loader-container">
              <div className="loader" />
            </div>
          ) : (
            <Container sx={{ mt: 4, mb: 4 }} className="mainContainerComp">
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", md: "center" },
                  gap: 2,
                  flexWrap: "wrap",
                  mb: 3,
                }}
              >
                <Box>
                  <h3 className="dashboard-title" style={{ marginBottom: 6 }}>
                    Tracking Analytics
                  </h3>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Inspect engagement, CTA usage, and journey flow across web pages
                    and mobile screens.
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {Object.entries(PLATFORM_OPTIONS).map(([key, option]) => (
                    <Button
                      key={key}
                      variant={platform === key ? "contained" : "outlined"}
                      onClick={() => handlePlatformChange(key)}
                      sx={{
                        borderRadius: "999px",
                        textTransform: "none",
                        px: 2.5,
                        ...(platform === key ? buttonActiveSx : buttonInactiveSx),
                      }}
                    >
                      {option.label}
                    </Button>
                  ))}
                </Stack>
              </Box>

              {errorMessage ? (
                <Alert severity="error" sx={{ mb: 2.5 }}>
                  {errorMessage}
                </Alert>
              ) : null}

              <Card sx={{ ...sectionCardSx, mb: 2.5 }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
                        Filters
                      </Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Narrow analytics by date, event type, and{" "}
                        {platformConfig.entityLabel.toLowerCase()} label.
                      </Typography>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          type="date"
                          label="Start Date"
                          InputLabelProps={{ shrink: true }}
                          value={draftFilters.startDate}
                          onChange={handleDraftChange("startDate")}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          type="date"
                          label="End Date"
                          InputLabelProps={{ shrink: true }}
                          value={draftFilters.endDate}
                          onChange={handleDraftChange("endDate")}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Event Type"
                          placeholder="page_view, cta_click..."
                          value={draftFilters.eventType}
                          onChange={handleDraftChange("eventType")}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label={`${platformConfig.entityLabel} Label`}
                          placeholder={platformConfig.entityPlaceholder}
                          value={draftFilters.label}
                          onChange={handleDraftChange("label")}
                        />
                      </Grid>
                    </Grid>

                    <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap">
                      <Button
                        variant="contained"
                        onClick={() => setFilters(draftFilters)}
                        sx={{ textTransform: "none", ...buttonActiveSx }}
                      >
                        Apply Filters
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setDraftFilters(INITIAL_FILTERS);
                          setFilters(INITIAL_FILTERS);
                        }}
                        sx={{ textTransform: "none" }}
                      >
                        Clear
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                {statCards.map((item) => (
                  <Grid item xs={12} sm={6} lg={3} key={item.title}>
                    <Card sx={sectionCardSx}>
                      <CardContent sx={{ p: 2.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            textTransform: "uppercase",
                            letterSpacing: 1,
                            color: "text.secondary",
                            fontWeight: 700,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 800, mt: 1 }}>
                          {item.value}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                          {item.helper}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={12} lg={4}>
                  <Card sx={sectionCardSx}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                        {platformConfig.topSectionTitle}
                      </Typography>
                      <Stack spacing={1.25}>
                        {topVisitedItems.length ? (
                          topVisitedItems.map((item, index) => {
                            const value = item?.[platformConfig.entityKey] || "Unlabeled";
                            return (
                              <Box
                                key={`${value}-${index}`}
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: 2,
                                  p: 1.5,
                                  borderRadius: 3,
                                  bgcolor: "rgba(248, 250, 252, 0.9)",
                                }}
                              >
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    {value}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    {platformConfig.entityLabel} label
                                  </Typography>
                                </Box>
                                <Chip label={item?.count || 0} size="small" />
                              </Box>
                            );
                          })
                        ) : (
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            No {platformConfig.entityLabel.toLowerCase()} activity found for
                            the selected filters.
                          </Typography>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} lg={4}>
                  <Card sx={sectionCardSx}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                        Top CTA / Button Interactions
                      </Typography>
                      <Stack spacing={1.25}>
                        {(summary?.topCtas || []).length ? (
                          summary.topCtas.map((item, index) => (
                            <Box
                              key={`${item?.label || "cta"}-${index}`}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 2,
                                p: 1.5,
                                borderRadius: 3,
                                bgcolor: "rgba(248, 250, 252, 0.9)",
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {item?.label || "Unknown CTA"}
                              </Typography>
                              <Chip label={item?.count || 0} size="small" />
                            </Box>
                          ))
                        ) : (
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            No CTA interaction data found for this view.
                          </Typography>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} lg={4}>
                  <Card sx={sectionCardSx}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                        Event Counts By Type
                      </Typography>
                      <Stack spacing={1.25}>
                        {(summary?.eventTypeCounts || []).length ? (
                          summary.eventTypeCounts.map((item, index) => (
                            <Box
                              key={`${item?.eventType || "type"}-${index}`}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 2,
                                p: 1.5,
                                borderRadius: 3,
                                bgcolor: "rgba(248, 250, 252, 0.9)",
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {item?.eventType || "unknown"}
                              </Typography>
                              <Chip label={item?.count || 0} size="small" />
                            </Box>
                          ))
                        ) : (
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            No event type totals available yet.
                          </Typography>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} lg={5}>
                  <Card sx={sectionCardSx}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: { xs: "flex-start", sm: "center" },
                          gap: 1,
                          mb: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            Recent Events Feed
                          </Typography>
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            Raw event activity from `/api/tracking/admin/events`.
                          </Typography>
                        </Box>
                        <Chip label={`${eventsData?.total || 0} total`} size="small" />
                      </Box>

                      <Stack spacing={1.5}>
                        {(eventsData?.events || []).length ? (
                          eventsData.events.map((event, index) => {
                            const eventLabel = getEventLabel(event, platformConfig.entityKey);
                            const userName =
                              typeof event?.userId === "object"
                                ? event?.userId?.name || event?.userId?.email
                                : null;

                            return (
                              <Box
                                key={event?.eventId || `${eventLabel}-${index}`}
                                sx={{
                                  p: 1.75,
                                  borderRadius: 3,
                                  border: "1px solid rgba(15, 23, 42, 0.08)",
                                  bgcolor: "#fff",
                                }}
                              >
                                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                                  <Chip
                                    label={event?.eventType || "unknown"}
                                    size="small"
                                    color="error"
                                    variant="outlined"
                                  />
                                  <Chip
                                    label={`${platformConfig.entityLabel}: ${eventLabel}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                  {event?.ctaName || event?.buttonLabel ? (
                                    <Chip
                                      label={`CTA: ${event?.ctaName || event?.buttonLabel}`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  ) : null}
                                </Stack>

                                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                  {formatDateTime(event?.timestamp || event?.createdAt)}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.75 }}>
                                  Session: {event?.sessionId || "N/A"}
                                </Typography>
                                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                                  {userName
                                    ? `User: ${userName}`
                                    : `Anonymous ID: ${event?.anonymousId || "N/A"}`}
                                </Typography>
                              </Box>
                            );
                          })
                        ) : (
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            No recent raw events found for this selection.
                          </Typography>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} lg={7}>
                  <Card sx={sectionCardSx}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: { xs: "flex-start", sm: "center" },
                          gap: 1,
                          mb: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            Journey Visibility
                          </Typography>
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            Session-level flow snapshots from `/api/tracking/admin/flows`.
                          </Typography>
                        </Box>
                        <Chip label={`${flowsData?.totalSessions || 0} sessions`} size="small" />
                      </Box>

                      <Stack spacing={1.75}>
                        {(flowsData?.flows || []).length ? (
                          flowsData.flows.map((flow, index) => (
                            <Box
                              key={flow?.sessionId || `flow-${index}`}
                              sx={{
                                p: 2,
                                borderRadius: 3,
                                border: "1px solid rgba(15, 23, 42, 0.08)",
                                bgcolor: "#fff",
                              }}
                            >
                              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                                <Chip label={platformConfig.label} size="small" />
                                <Chip label={`${flow?.eventCount || 0} events`} size="small" />
                                <Chip
                                  label={`Duration: ${formatDuration(flow?.startedAt, flow?.endedAt)}`}
                                  size="small"
                                  variant="outlined"
                                />
                              </Stack>

                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                Session: {flow?.sessionId || "N/A"}
                              </Typography>
                              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                                Started {formatDateTime(flow?.startedAt)} and ended{" "}
                                {formatDateTime(flow?.endedAt)}
                              </Typography>

                              <Divider sx={{ my: 1.5 }} />

                              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                {(flow?.events || []).map((event, eventIndex) => (
                                  <Chip
                                    key={event?.eventId || `${flow?.sessionId}-${eventIndex}`}
                                    label={`${eventIndex + 1}. ${getEventLabel(
                                      event,
                                      platformConfig.entityKey
                                    )}`}
                                    variant="outlined"
                                  />
                                ))}
                              </Stack>
                            </Box>
                          ))
                        ) : (
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            No session journeys are available for the current filters.
                          </Typography>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Container>
          )}
        </Box>
      </div>
    </Box>
  );
};

export default TrackingAnalytics;
